<!-- Responsibility: Implement trait card behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->
<trait-card>
  <div
    class="card { props.flipping ? 'flipping' : '' }"
    role="button"
    tabindex="0"
    aria-label={ `Open trait ${props.trait.name}` }
    onclick={ openDetails }
    onkeydown={ onKeydown }
  >
    <div class="trait-copy">
      <b>{ props.trait.name.toUpperCase() }</b>
      <small>{ shortDesc() }</small>
    </div>
    <span class="trait-mark" aria-hidden="true">⊕</span>
  </div>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { buildInfoDialog } from '../dialog/dialog-openers.ts'
    import { TOPICS } from "@anyquest/shared-core"

    export default {
      shortDesc() {
        const desc = String(this.props?.trait?.desc ?? '').trim()
        if (!desc) return 'Inspect trait details'
        return desc.length > 72 ? `${desc.slice(0, 69)}...` : desc
      },

      openDetails() {
        dispatcher.trigger('trait-card', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: this.props.trait.name,
            desc: this.props.trait.desc,
          },
        }))
      },

      onKeydown(e) {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        this.openDetails()
      },
    }
  </script>

  <style>
    .card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-sm);
      min-height: 4.75rem;
      padding: 0.9rem 1rem;
      border-radius: 1rem;
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-high), transparent);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-raised) var(--alpha-high), transparent), color-mix(in srgb, var(--bg) var(--alpha-high), transparent)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) 12%, transparent), transparent 58%);
      box-shadow:
        inset 0 1px 0 color-mix(in srgb, #f8fbff 12%, transparent),
        0 0.5rem 1.1rem color-mix(in srgb, #020611 20%, transparent);
      cursor: pointer;
      transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
    }

    .card:hover,
    .card:focus-visible {
      border-color: color-mix(in srgb, var(--gold) 56%, var(--border));
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-raised) 92%, transparent), color-mix(in srgb, var(--bg) 96%, transparent)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) 22%, transparent), transparent 60%);
      box-shadow:
        inset 0 1px 0 color-mix(in srgb, #f8fbff 16%, transparent),
        0 0.7rem 1.35rem color-mix(in srgb, #020611 28%, transparent);
      transform: translateY(-0.08rem);
      outline: none;
    }

    .trait-copy {
      display: flex;
      flex-direction: column;
      gap: 0.28rem;
      min-width: 0;
    }

    .trait-copy b {
      color: color-mix(in srgb, var(--gold) 82%, #fff4cf);
      font-size: 0.78rem;
      letter-spacing: 0.12em;
      line-height: 1.2;
    }

    .trait-copy small {
      color: color-mix(in srgb, var(--text-muted) 94%, #dce7fa);
      font-size: 0.8rem;
      line-height: 1.35;
    }

    .trait-mark {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 62.4375rem;
      border: 1px solid color-mix(in srgb, var(--gold) 34%, transparent);
      color: color-mix(in srgb, var(--gold) 72%, #fff0bf);
      background: color-mix(in srgb, var(--gold) 10%, transparent);
      flex: 0 0 auto;
    }

    .card.flipping {
      animation: trait-flip 0.6s ease-in-out;
    }

  </style>
</trait-card>
